/**
 * Auth session store.
 *
 * Owns: access/refresh tokens, user profile, session status.
 * Provides: login(), logout(), getValidAccessToken(), forceRefresh().
 *
 * Concurrency model — singleton refresh promise:
 *   Any number of concurrent requests that need a refresh await the SAME
 *   in-flight refresh; only one POST /connect/token fires per refresh wave.
 *
 * Expiry model — proactive + reactive:
 *   getValidAccessToken() pre-flights the JWT exp claim and refreshes if
 *   the token expires within 30s. The api-client also calls forceRefresh()
 *   on a 401 as a safety net (revoked tokens, clock skew).
 */

import { create } from 'zustand';
import { authStorage } from './storage';
import { isExpiringSoon, userDataFromToken } from './jwt';
import {
  login as apiLogin,
  refreshTokens,
  getUserData,
  checkUserData,
  updateDeviceDetails,
  updateUserTimeZone,
  checkLockOutTime,
  ActiveSessionConflictError,
  ApiAuthError,
  type UserData,
} from './auth-api';
import { DEVICE_ID_WEB, DEVICE_TYPE_WEB, getLocalTimezone } from './timezone';
import { SCRIPS_ADMIN_ROLE } from './roles';
import { DEV_AUTO_LOGIN_ENABLED, MANUAL_LOGIN_REQUESTED } from './dev-auto-login';
import { DEV_PERSONA_LOGIN } from './dev-persona-login';
import { clearLastNav } from '@/app/lib/last-selected-nav';


/** Flutter `Timezone_mismatch_key` — empty string sentinel set on
 *  user.timeZone after the post-login UpdateUserTimeZone POST fires.
 *  scrips_core/lib/constants/app_constants.dart:109. */
const TIMEZONE_MISMATCH_KEY = '';

/** Flutter PM appType literal. */
const APP_TYPE_PRACTICE_MANAGEMENT = 'Practice Management';

/**
 * Idle-timeout session cap (Flutter `timeoutInterceptor`,
 * dio_api_client.dart:291-342). If no authenticated request fires for this
 * long, the next one expires the session. Flutter uses 120 min for BOTH beta
 * and non-beta orgs (`:309-310` — the "15m" code comment there is dead; both
 * branches evaluate to 120). Any request within the window resets the clock.
 */
const IDLE_LIMIT_MS = 120 * 60 * 1000;

/**
 * Thrown by `getValidAccessToken()` when the idle cap is hit on a still-valid
 * token. The session-expired modal (F-AUT-089) is already up by the time this
 * throws; the in-flight request rejects with this so callers can distinguish
 * an idle expiry from a normal auth failure. Mirrors Flutter's
 * `DioException("Session expired, exceeded idle time limit")` (:329-331).
 */
export class SessionIdleExpiredError extends Error {
  constructor() {
    super('Session expired — idle time limit exceeded');
    this.name = 'SessionIdleExpiredError';
  }
}

/**
 * Phase 7 post-refresh re-sync timeout (Samer review PR #320 item 2).
 * Bounds the GetUserDataToken call so a hung profile endpoint can't block
 * token rotation. 3s is generous vs the typical sub-200ms response and
 * still well below user-perceptible "stuck" thresholds.
 */
const PROFILE_RESYNC_TIMEOUT_MS = 3000;

/**
 * Thrown by the post-refresh re-sync Promise.race when GetUserDataToken
 * doesn't resolve within `PROFILE_RESYNC_TIMEOUT_MS`. Caught + swallowed
 * by the re-sync catch as "transient" — same treatment as a 5xx response.
 */
class ProfileResyncTimeoutError extends Error {
  constructor() {
    super(`Profile re-sync timed out after ${PROFILE_RESYNC_TIMEOUT_MS}ms`);
    this.name = 'ProfileResyncTimeoutError';
  }
}

/**
 * Shallow-merge `apiUser` into `current`, but drop keys whose value is
 * `null` or `undefined` so a partial profile response doesn't overwrite
 * valid cached fields (permissions, isBeta, organizationLocations).
 *
 * Samer review PR #320 item 3: the default `{ ...current, ...apiUser }`
 * spread would let an explicit `null` from the backend (e.g. a
 * partially-populated GetUserDataToken response during a backend
 * deployment) blow away a cached permission set, creating a transient
 * PHI-access regression. This helper keeps `apiUser`'s present values
 * and ignores its nullish ones.
 */
function mergePreservingNonNull(
  current: UserData,
  apiUser: UserData,
): UserData {
  const out: Record<string, unknown> = { ...current };
  for (const [k, v] of Object.entries(apiUser)) {
    if (v !== null && v !== undefined) {
      out[k] = v;
    }
  }
  return out as unknown as UserData;
}

type Status =
  | 'unauthenticated'
  | 'authenticating'
  | 'authenticated'
  | 'refreshing';

/**
 * State carried while `<ActiveSessionConflictDialog />` is open. Mirrors
 * Flutter's partial-user surfacing (`login_repository_impl.dart:78-93`):
 * 409 from `GetUserDataToken` / `CheckUserDataToken` returns a body with
 * the conflicting `userId`, which the dialog uses to call
 * `UpdateDeviceDetails` and clear the `User_Tokens` row before retrying.
 *
 * Cleared on `confirmTakeover()` success, `cancelTakeover()`, or any
 * `logout()`. Never persisted to localStorage — purely in-memory.
 */
export interface ActiveSessionConflict {
  /** The user the backend says is already logged in. Used as the
   *  `userId` body field on the `UpdateDeviceDetails` POST. */
  conflictUserId: string;
}

/** PROD-1026 (F-AUT-046): timezone-mismatch recovery state. */
interface TimezoneMismatch {
  firstName: string | null;
}

interface AuthState {
  status: Status;
  accessToken: string | null;
  refreshToken: string | null;
  user: UserData | null;
  /**
   * Epoch-ms of the last authenticated request (idle-timeout clock). Set on
   * login + every successful refresh, and bumped by each request inside the
   * idle window. Persisted so a returning tab still expires past the cap.
   */
  lastActivityAt: number;
  activeSessionConflict: ActiveSessionConflict | null;
  /**
   * PROD-1026 (F-AUT-046): non-null when login detected a device/saved
   * timezone mismatch; drives the recovery popup. Login does NOT complete
   * while this is set — the user must confirm (clear local data + re-login).
   */
  timezoneMismatch: TimezoneMismatch | null;
  /**
   * F-AUT-089: true when a token refresh failed on an active session —
   * drives the session-expired modal (Flutter `showSessionExpiredPopup`,
   * `session_helper.dart:194-207`). The session is NOT cleared while this
   * is set; the user must acknowledge (OK → `confirmSessionExpired` →
   * logout + redirect). React has no idle timer, so unlike Flutter this
   * only fires on refresh-failure, never on a 15-min idle timeout.
   */
  sessionExpired: boolean;

  login: (username: string, password: string) => Promise<void>;
  /**
   * Logs the user out. Async because we POST to `UpdateDeviceDetails`
   * BEFORE clearing storage (Flutter parity, `login_data_source_impl.dart:
   * 325-344`). Best-effort: the POST is wrapped in try/catch and storage
   * is cleared regardless of failure — the user must always be able to
   * log out, even when offline.
   */
  logout: () => Promise<void>;
  /** Return a non-expiring access token, refreshing first if needed. */
  getValidAccessToken: () => Promise<string>;
  /** Force a refresh regardless of expiry — used by api-client on 401. */
  forceRefresh: () => Promise<string>;
  /**
   * PROD-1026: user confirmed the timezone-mismatch popup ("Ok to relogin").
   * Clears local data + re-issues login; the server-side tz was already
   * updated, so the fresh login matches and completes. Mirrors Flutter
   * `login_screen.dart:133-136` (clearLocalDataWithoutChangingScreen + DoLoginEvent).
   */
  confirmTimezoneRelogin: () => Promise<void>;
  /**
   * F-AUT-089: user acknowledged the session-expired modal ("Ok"). Clears
   * the flag and completes the logout (UpdateDeviceDetails + storage clear
   * + redirect to /login). Mirrors Flutter `showSessionExpiredPopup`'s
   * `afterYes` callback. No-op if no session-expired state is pending.
   */
  confirmSessionExpired: () => Promise<void>;
  /**
   * Mark the practice-setup onboarding wizard as complete (PROD-930).
   * Purely local — Flutter parity: `menu_container_screen.dart:451-457`
   * writes `loggedUserData.isOnboarded = true` + persists via
   * `AppPreferenceUtil.writeString(scripsLoggedPMUser, …)`. No backend.
   *
   * Bails silently if `user` is null (race condition / corrupted storage /
   * logged-out tab — App.tsx's wizard-required gate gates on `user?.role`
   * so this should be unreachable, but the guard is defensive).
   *
   * Returns true iff the write happened — callers can use it as a
   * post-condition check (e.g. for tests).
   */
  markOnboardingComplete: () => boolean;

  /**
   * Switch the active practice (the scheduling toolbar practice dropdown).
   * Merges `practiceId` + `practiceName` into `user` and persists, mirroring
   * Flutter `PracticeChangedState` (`calendar_screen.dart:2436-2453`):
   * sets `userData.practiceId` / `userData.practiceName`, writes the updated
   * user to storage, then lets the calendar refetch for the new practice.
   *
   * Purely local — there is no backend call. The api-client reads
   * `user.practiceId` from this store at request time, so the next request
   * carries the new `PracticeId` header; any component selecting
   * `user.practiceId` re-renders and re-keys its practice-scoped queries.
   *
   * No-op (returns false) if `user` is null or the id is unchanged.
   */
  setActivePractice: (practice: {
    practiceSetupId: string;
    practiceName: string | null;
  }) => boolean;

  /**
   * User confirmed the `<ActiveSessionConflictDialog />` "Yes" CTA —
   * kick the conflicting session and complete the in-flight auth flow.
   *
   * Mirrors Flutter `login_screen.dart:146-147` + `:177-182`:
   * `LogoutEvent(state.response)` → `UpdateDeviceDetails(userId)` → on
   * `LogoutState`, `Future.delayed(200ms) → DoLoginEvent(email, password)`.
   *
   * No-op if no conflict is pending.
   */
  confirmTakeover: () => Promise<void>;

  /**
   * User declined the conflict dialog — clear local session and bounce
   * back to login. Mirrors Flutter `:148-150` (`afterNo` callback).
   */
  cancelTakeover: () => Promise<void>;
}

/**
 * Module-local resolvers for the in-flight `login()` Promise when a 409
 * conflict suspends the flow waiting for user confirmation. Only one
 * conflict can be pending at a time (the dialog is modal).
 *
 * Holds the credentials needed to retry login after the takeover
 * succeeds — Flutter mirrors this by capturing email+password in
 * `LoginScreen` state via `state.email / state.password`
 * (`login_screen.dart:62-65` + `:179-182`). The values live only while
 * the dialog is open and are cleared as soon as the conflict resolves.
 */
interface PendingConflictContext {
  conflictUserId: string;
  accessToken: string;
  refreshToken: string | null;
  /** Login path: credentials to re-issue `/connect/token`. Null on the
   *  boot / refresh re-sync paths (no creds in scope; we retry the
   *  profile fetch instead). */
  retryCredentials: { username: string; password: string } | null;
  resolve: () => void;
  reject: (err: Error) => void;
}

let pendingConflict: PendingConflictContext | null = null;

/**
 * PROD-1026: parked login context while the timezone-mismatch popup is open.
 * `resolve`/`reject` settle the original `login()` promise once the user
 * confirms (re-login) or the flow is torn down (logout).
 */
let pendingTzMismatch:
  | {
      retryCredentials: { username: string; password: string };
      accessToken: string;
      userId: string;
      firstName: string | null;
      resolve: () => void;
      reject: (e: Error) => void;
    }
  | null = null;

let refreshInFlight: Promise<string> | null = null;

const rawAccess = authStorage.getAccessToken();
const rawRefresh = authStorage.getRefreshToken();
const persistedLastActivity = authStorage.getLastActivity();

/**
 * Cold-boot idle expiry: if a stored session has been idle past the cap, end
 * it BEFORE the app mounts — clear storage and boot unauthenticated so the
 * router lands on /login. Without this the authenticated shell rendered and
 * fired data queries against the dead session, so the user saw a broken
 * "couldn't load…" page + 401 console noise behind the session-expired modal.
 *
 * Guard: only fires when a `last_activity` timestamp actually exists and is
 * stale. Existing pre-feature sessions (no `last_activity` key) are NOT
 * expired on upgrade — `getLastActivity()` returns null → skipped.
 *
 * ORDERING GUARANTEE (CoS review PR #670): this runs SYNCHRONOUSLY at module
 * evaluation — the store must be imported before the app renders, and the
 * clear + `initialAccess = null` below happen inline here, so there is no
 * authenticated API call in flight when it runs. The first request can only
 * come from a mounted component, which mounts after this module has fully
 * evaluated. No race → no spurious 401 / partial auth state on boot.
 */
const bootIdleExpired =
  rawAccess !== null &&
  persistedLastActivity !== null &&
  Date.now() - persistedLastActivity > IDLE_LIMIT_MS;

/**
 * `?login=manual` must actually REACH the login form.
 *
 * Suppressing the next auto-login is not enough on a deployed dev build that has
 * already auto-logged the QA account: its token is still in storage, so the store
 * boots authenticated and the router redirects straight past /login. The person
 * who typed `?login=manual` sees the app they were trying to get out of, and
 * nothing tells them they must first log out — which is exactly what happened
 * when this escape was first shipped. Caught by the Codex review gate.
 *
 * Clearing here rather than in a component keeps the same synchronous
 * module-evaluation ordering guarantee the idle path documents above: no
 * authenticated request can be in flight, so there is no spurious 401.
 *
 * Asking for the login form is a deliberate act by the person at the keyboard,
 * so ending the current session is the intent, not a side effect.
 */
const bootManualLogin = MANUAL_LOGIN_REQUESTED && rawAccess !== null;

/**
 * `?devlogin=<persona>` (dev-persona-login.ts) replaces whatever session is
 * stored, so a tab already signed in as one persona can switch to another. Null
 * everywhere except a dev build on the dev host with a known persona. Same
 * synchronous boot-time clear as `?login=manual` above, for the same reason.
 */
const bootPersonaLogin = DEV_PERSONA_LOGIN !== null && rawAccess !== null;

if (bootIdleExpired || bootManualLogin || bootPersonaLogin) {
  authStorage.clear();
  clearLastNav();
}

const bootCleared = bootIdleExpired || bootManualLogin || bootPersonaLogin;
const initialAccess = bootCleared ? null : rawAccess;
const initialRefresh = bootCleared ? null : rawRefresh;
const initialUser = bootCleared
  ? null
  : (authStorage.getUser<UserData>() ??
    (initialAccess ? userDataFromToken(initialAccess) : null));
// Idle clock: resume the persisted value if present. Absent (first boot on
// this device, or just cleared above) → seed to "now" so a returning session
// isn't insta-expired before the first request establishes a real timestamp.
const initialLastActivity = authStorage.getLastActivity() ?? Date.now();

export const useAuthStore = create<AuthState>((set, get) => {
  /**
   * Apply a fresh `LoginTokens` set into the store: write secure storage,
   * fetch the rich profile, merge JWT + API user, fire the timezone
   * sync, and flip `status` to `authenticated`. Shared between the
   * initial `login()` flow and the post-takeover retry in
   * `confirmTakeover()`.
   *
   * On `ActiveSessionConflictError` from `getUserData`, parks the flow
   * in `pendingConflict` and returns a Promise that resolves when the
   * user confirms (and the takeover retry succeeds) or rejects on
   * cancel. The login-page caller continues to `await login()` — the
   * promise just doesn't resolve until the dialog is resolved.
   *
   * `retryCredentials` is the email+password the user typed (login path
   * only). On takeover we re-issue `/connect/token` with these — matches
   * Flutter `login_screen.dart:179-182` re-firing `DoLoginEvent` after
   * `LogoutState`. Boot / refresh paths pass null; the takeover retries
   * the profile fetch on the existing token instead.
   */
  const applyTokens = async (
    tokens: { accessToken: string; refreshToken: string },
    retryCredentials: { username: string; password: string } | null,
    opts: { skipTzPopup?: boolean; takeoverAlreadyAttempted?: boolean } = {},
  ): Promise<void> => {
    // Persist tokens immediately so the api-client + boot path see them.
    // The user blob is filled in below once getUserData lands.
    authStorage.setTokens(tokens.accessToken, tokens.refreshToken);

    // JWT claims as the floor — guaranteed to populate userId / orgId
    // even if GetUserDataToken fails. API response (with rich profile
    // fields, including practiceId) takes precedence on overlapping keys.
    const fromJwt =
      userDataFromToken(tokens.accessToken) ?? ({} as UserData);

    let user: UserData = fromJwt;
    try {
      const apiUser = await getUserData(tokens.accessToken);
      user = { ...fromJwt, ...apiUser };
    } catch (err) {
      if (err instanceof ActiveSessionConflictError) {
        // DEV-ONLY auto-takeover: instead of parking on the confirmation
        // dialog (the production behavior), automatically kick the
        // conflicting session and retry. The Identity backend has NO
        // session-skip bypass — the old `SkipConflict` header was removed in
        // PROD-931 (GenerateUserTokenDataCommandHandler.cs) — so the only way
        // past the 409 is the takeover: POST UpdateDeviceDetails, which sets
        // `IsUserLoggedIn = false` server-side (AccountController.cs:719), then
        // re-run /connect/token → GetUserDataToken (the recursion below
        // re-enters applyTokens and now passes the IsUserLoggedIn check). This
        // lets an engineer/agent reach the app without hand-confirming the
        // dialog. Requires the login credentials (login path only). ONE
        // attempt: `takeoverAlreadyAttempted` is set on the retry so a second
        // 409 falls through to the normal dialog rather than looping. Disabled
        // in production (`DEV_AUTO_LOGIN_ENABLED` is false) — the takeover-
        // confirmation dialog is preserved there. A deployed-dev `?devlogin`
        // persona sign-in takes over the same way: no one is at the keyboard.
        if (
          (DEV_AUTO_LOGIN_ENABLED || DEV_PERSONA_LOGIN !== null) &&
          retryCredentials &&
          !opts.takeoverAlreadyAttempted
        ) {
          await updateDeviceDetails(tokens.accessToken, {
            userId: err.conflictUserId,
            appType: APP_TYPE_PRACTICE_MANAGEMENT,
            deviceId: DEVICE_ID_WEB,
          });
          const fresh = await apiLogin(
            retryCredentials.username,
            retryCredentials.password,
          );
          return await applyTokens(fresh, retryCredentials, {
            ...opts,
            takeoverAlreadyAttempted: true,
          });
        }
        // Park flow — wait for user confirmation via the dialog.
        return new Promise<void>((resolve, reject) => {
          pendingConflict = {
            conflictUserId: err.conflictUserId,
            accessToken: tokens.accessToken,
            refreshToken: tokens.refreshToken,
            retryCredentials,
            resolve,
            reject,
          };
          set({
            activeSessionConflict: { conflictUserId: err.conflictUserId },
          });
        });
      }
      // An authoritative "this user may not proceed" is NOT a transport
      // failure, and must not be absorbed by the resilience path below.
      // GetUserDataToken answers 401/403 for an archived, deactivated or
      // revoked account, so falling through would seat that user in the app
      // shell on JWT claims alone. Flutter refuses the login outright here
      // (login_repository_impl.dart:58-68 clears the stored token and returns
      // the archived/inactive failure), and this store already draws exactly
      // this distinction on the refresh re-sync path (see doRefresh below).
      // `applyTokens` was the one profile-fetch call site missing it.
      //
      // The clear is load-bearing: tokens were persisted at the top of this
      // function, and login()'s catch only flips status to 'unauthenticated'
      // without touching storage — so without this, the next page load would
      // re-read the token and boot 'authenticated' with a JWT-only user.
      if (
        err instanceof ApiAuthError &&
        (err.status === 401 || err.status === 403)
      ) {
        authStorage.clear();
        clearLastNav();
        throw err;
      }
      // Genuine transport/server failure (5xx, 408, 429, dropped socket) —
      // keep the JWT-only user. The api-client will omit the PracticeId
      // header until a successful refresh; the backend will reject
      // practice-scoped reads with a clear error rather than silently
      // leaking another tenant's data.
      //
      // Caveat, deliberately not papered over: role gates cannot match a
      // JWT-only user (see jwt.ts — `role` is intentionally absent), and in
      // scheduling that means a doctor's self-scoping filter no-ops rather
      // than denying, widening the visible set within their own tenant.
      // Tenant isolation still holds; provider-level narrowing does not.
    }

    // Timezone-mismatch sync (PROD-862, Flutter login_data_source_impl.dart:
    // 218-229). Three branches verbatim from Flutter:
    //   1. JWT has no zoneinfo → set user.timeZone = currentTimezone
    //      (no API call needed)
    //   2. Device tz != JWT zoneinfo AND user is not Scrips admin →
    //      fire UpdateUserTimeZone (best-effort) + set user.timeZone
    //      to the empty-string sentinel `Timezone_mismatch_key`
    //   3. Match → set user.timeZone = currentTimezone
    const currentTimezone = getLocalTimezone();
    const jwtZoneinfo = fromJwt.timeZone;
    if (!jwtZoneinfo) {
      user = { ...user, timeZone: currentTimezone };
    } else if (
      currentTimezone !== jwtZoneinfo &&
      user.role !== SCRIPS_ADMIN_ROLE &&
      user.userId
    ) {
      void updateUserTimeZone(tokens.accessToken, {
        userId: user.userId,
        timeZone: currentTimezone,
        deviceToken: null,
        deviceType: DEVICE_TYPE_WEB,
        deviceId: DEVICE_ID_WEB,
      }).catch(() => {
        // Silent — retried on next refresh.
      });
      // PROD-1026 (F-AUT-046): on the LOGIN path, don't silently complete
      // login with the mismatch sentinel — park and surface the recovery
      // popup. On confirm we clear local data + re-login; the tz was just
      // updated server-side so the fresh JWT's zoneinfo matches and login
      // completes. Mirrors Flutter `login_screen.dart:128-138`. The
      // `skipTzPopup` guard (set on the confirm re-login) prevents an
      // infinite popup loop if the update hasn't propagated yet. Boot/refresh
      // never reach here (they use checkUserData, not applyTokens).
      if (retryCredentials && !opts.skipTzPopup && user.userId) {
        const firstName = user.firstName ?? null;
        const uid = user.userId;
        return await new Promise<void>((resolve, reject) => {
          pendingTzMismatch = {
            retryCredentials,
            accessToken: tokens.accessToken,
            userId: uid,
            firstName,
            resolve,
            reject,
          };
          set({ timezoneMismatch: { firstName } });
          // A deployed-dev `?login=<persona>` sign-in has no one to press OK
          // (dev-persona-login.ts), so confirm the popup as a person would.
          // The shared dev accounts flip timezone per login, so without this
          // a persona whose token zone differs from the browser never lands.
          if (DEV_PERSONA_LOGIN !== null) {
            queueMicrotask(() => void get().confirmTimezoneRelogin());
          }
        });
      }
      user = { ...user, timeZone: TIMEZONE_MISMATCH_KEY };
    } else {
      user = { ...user, timeZone: currentTimezone };
    }

    authStorage.setSession(tokens.accessToken, tokens.refreshToken, user);
    // Fresh session → start the idle clock now.
    const loginAt = Date.now();
    authStorage.setLastActivity(loginAt);
    set({
      status: 'authenticated',
      accessToken: tokens.accessToken,
      refreshToken: tokens.refreshToken,
      user,
      lastActivityAt: loginAt,
      activeSessionConflict: null,
    });
  };

  const doRefresh = async (): Promise<string> => {
    if (refreshInFlight) return refreshInFlight;
    // F-AUT-089: once the session-expired modal is up, stop attempting
    // refresh — every authed request would otherwise re-fire a doomed
    // refresh round-trip behind the (non-dismissable) modal. Fail fast
    // until the user acknowledges and logs out.
    if (get().sessionExpired) throw new Error('Session expired');

    set({ status: 'refreshing' });
    refreshInFlight = (async () => {
      const refreshToken = get().refreshToken;
      if (!refreshToken) {
        // F-AUT-089: a missing refresh token on an active session = expired
        // session. Park on the modal (do NOT silently clear) so the user
        // sees why they're being bounced; OK drives the logout. If there's
        // no user blob the session is already gone — fall back to the old
        // silent logout to avoid a modal with nothing behind it.
        if (get().user) {
          set({ status: 'authenticated', sessionExpired: true });
        } else {
          void get().logout();
        }
        throw new Error('No refresh token');
      }
      try {
        const tokens = await refreshTokens(refreshToken);
        authStorage.setTokens(tokens.accessToken, tokens.refreshToken);
        // A successful refresh resets the idle clock (Flutter rewrites
        // `lastLoginTimeKey` on refresh, dio_api_client.dart:280-288). Load-
        // bearing for the "token expired + idle" path: `noteActivity` defers
        // to refresh without bumping, so without this reset the very next
        // request would idle-expire a session that just refreshed.
        const refreshedAt = Date.now();
        authStorage.setLastActivity(refreshedAt);
        set({
          status: 'authenticated',
          accessToken: tokens.accessToken,
          refreshToken: tokens.refreshToken,
          lastActivityAt: refreshedAt,
        });

        // PROD-894 Phase 7 — post-refresh profile re-sync. Flutter
        // `login_data_source_impl.dart:290` calls `getUserData` AFTER
        // every `RefreshTokenUseCase` so permissions / org-locations /
        // isBeta / etc. propagate when they change mid-session.
        //
        // Samer review (PR #320) — narrowed silent-fail + timeout guard:
        //   - 3s timeout via Promise.race so a hung profile endpoint
        //     doesn't block token rotation indefinitely.
        //   - 401 / 403 are NOT swallowed: 401 = the fresh access token is
        //     already invalid (user archived between refresh and re-sync);
        //     403 = permissions stripped server-side. Either case must
        //     bubble so the outer catch logs out cleanly.
        //   - 409 = active-session conflict. Park flow + open dialog
        //     (PROD-946). Caller's promise resolves once user confirms.
        //   - Transient failures (5xx / network / timeout) keep the
        //     existing user blob (JWT floor is still valid).
        //   - Shallow merge drops nullish keys from the response so a
        //     partial profile payload doesn't overwrite valid cached
        //     fields (permissions, isBeta, organizationLocations).
        try {
          const apiUser = await Promise.race([
            getUserData(tokens.accessToken),
            new Promise<never>((_, reject) =>
              setTimeout(
                () => reject(new ProfileResyncTimeoutError()),
                PROFILE_RESYNC_TIMEOUT_MS,
              ),
            ),
          ]);
          const current = get().user ?? ({} as UserData);
          const merged = mergePreservingNonNull(current, apiUser);
          authStorage.setUser(merged);
          set({ user: merged });
        } catch (resyncErr) {
          if (resyncErr instanceof ActiveSessionConflictError) {
            // Park the refresh flow on the conflict dialog. The user's
            // existing access token stays valid until they resolve.
            await new Promise<void>((resolve, reject) => {
              pendingConflict = {
                conflictUserId: resyncErr.conflictUserId,
                accessToken: tokens.accessToken,
                refreshToken: tokens.refreshToken,
                retryCredentials: null,
                resolve,
                reject,
              };
              set({
                activeSessionConflict: {
                  conflictUserId: resyncErr.conflictUserId,
                },
              });
            });
          } else if (
            resyncErr instanceof ApiAuthError &&
            (resyncErr.status === 401 || resyncErr.status === 403)
          ) {
            // Session revoked / permissions stripped — bubble to the
            // outer catch so logout() fires and the UI doesn't keep
            // stale PHI visible.
            throw resyncErr;
          }
          // Transient (5xx / network / 3s timeout) — keep cached user
          // blob; next refresh / login round retries.
        }

        return tokens.accessToken;
      } catch (err) {
        // F-AUT-089: a genuine refresh / re-sync failure on an active
        // session → session expired. Park on the modal (Flutter
        // `showSessionExpiredPopup`) instead of a silent logout; OK drives
        // the logout + redirect.
        //
        // Guard on `get().user`: if the session was already torn down here
        // (e.g. the user cancelled the active-session-conflict dialog,
        // whose reject propagates into this catch after logout() already
        // ran), don't resurrect a second modal — just rethrow.
        if (get().user) {
          set({ status: 'authenticated', sessionExpired: true });
        }
        throw err;
      } finally {
        refreshInFlight = null;
      }
    })();
    return refreshInFlight;
  };

  /**
   * Idle-timeout gate — Flutter `timeoutInterceptor` (dio_api_client.dart:
   * 291-342) ported to the per-request choke point. Called from
   * `getValidAccessToken()` on every authenticated request, BEFORE the
   * proactive refresh (so an idle-but-refreshable session still expires).
   *
   *   - Only authenticated sessions are gated.
   *   - `now - lastActivityAt > IDLE_LIMIT_MS` AND the token is NOT already
   *     expired → expire (set `sessionExpired`, F-AUT-089 modal). Returns
   *     `'idle-expired'`.
   *   - Idle but the token is ALREADY expired → skip the idle error and let
   *     the normal refresh/401 path run (Flutter `:312-314`). Returns `'ok'`.
   *   - Within the window → bump the clock (any request resets it, Flutter
   *     `:335-337`) and return `'ok'`.
   */
  const noteActivity = (): 'ok' | 'idle-expired' => {
    const s = get();
    if (s.status !== 'authenticated' || !s.accessToken) return 'ok';
    // Modal already up — keep blocking without re-touching the clock.
    if (s.sessionExpired) return 'idle-expired';

    const now = Date.now();
    const last = s.lastActivityAt;
    if (Number.isFinite(last) && now - last > IDLE_LIMIT_MS) {
      // Token already expired → defer to the refresh/401 path (which resets
      // the clock on success); don't raise an idle error here.
      if (isExpiringSoon(s.accessToken, 0)) return 'ok';
      if (s.user) set({ sessionExpired: true });
      return 'idle-expired';
    }

    set({ lastActivityAt: now });
    authStorage.setLastActivity(now);
    return 'ok';
  };

  return {
    status: initialAccess ? 'authenticated' : 'unauthenticated',
    accessToken: initialAccess,
    refreshToken: initialRefresh,
    user: initialUser,
    lastActivityAt: initialLastActivity,
    activeSessionConflict: null,
    timezoneMismatch: null,
    sessionExpired: false,

    login: async (username, password) => {
      set({ status: 'authenticating' });
      try {
        const tokens = await apiLogin(username, password);
        // applyTokens handles the post-/connect/token chain. On an
        // active-session conflict it parks the flow on `pendingConflict`
        // and returns a promise that only resolves when the user confirms
        // (the dialog mounted at App root drives the resolution).
        await applyTokens(tokens, { username, password });
      } catch (err) {
        set({ status: 'unauthenticated' });
        // Map `/connect/token` HTTP failure → Flutter-verbatim error strings
        // (`login_repository_impl.dart:58-101`). The PM login page catches
        // the rethrown Error and toasts via `showServerError`.
        if (err instanceof ApiAuthError) {
          throw await mapLoginError(err, username);
        }
        throw err;
      }
    },

    logout: async () => {
      // POST UpdateDeviceDetails BEFORE clearing storage (Flutter
      // login_data_source_impl.dart:325-344). Best-effort: any failure
      // here must NOT prevent the user from logging out locally.
      const { accessToken, user } = get();
      if (accessToken && user?.userId) {
        try {
          await updateDeviceDetails(accessToken, {
            userId: user.userId,
            appType: APP_TYPE_PRACTICE_MANAGEMENT,
            deviceId: DEVICE_ID_WEB,
          });
        } catch {
          // Audit POST failed — proceed with local clear. Backend reconciles
          // on next login or via the SignalR force-logout flow.
        }
      }

      authStorage.clear();
      // Flutter clears `lastSelectedMenu` on logout (so the next user on a
      // shared browser doesn't inherit this user's last menu). Mirror it.
      clearLastNav();
      refreshInFlight = null;
      // Bail any waiting login/refresh promise if the user logged out
      // mid-conflict via some other path (force-logout SignalR, etc.).
      if (pendingConflict) {
        pendingConflict.reject(new Error('Session cleared'));
        pendingConflict = null;
      }
      if (pendingTzMismatch) {
        pendingTzMismatch.reject(new Error('Session cleared'));
        pendingTzMismatch = null;
      }
      set({
        status: 'unauthenticated',
        accessToken: null,
        refreshToken: null,
        user: null,
        activeSessionConflict: null,
        timezoneMismatch: null,
        sessionExpired: false,
      });
    },

    confirmSessionExpired: async () => {
      // F-AUT-089: user tapped "Ok" on the session-expired modal. Clear the
      // flag up front so the (non-dismissable) dialog dismisses immediately,
      // then complete the logout (UpdateDeviceDetails + storage clear +
      // status → unauthenticated, which the route guard turns into a /login
      // redirect). No-op if nothing is pending.
      if (!get().sessionExpired) return;
      set({ sessionExpired: false });
      await get().logout();
    },

    confirmTakeover: async () => {
      const ctx = pendingConflict;
      if (!ctx) return;
      // Clear store-visible conflict state immediately so the dialog
      // can flip to its in-flight spinner / dismiss; the module-local
      // `pendingConflict` is still set so we know how to resume.
      set({ activeSessionConflict: null });
      try {
        // Flutter `login_screen.dart:147` → LogoutEvent(state.response).
        // Body posts `userId` of the conflicting session; backend clears
        // the matching `User_Tokens` row so a fresh login won't 409.
        await updateDeviceDetails(ctx.accessToken, {
          userId: ctx.conflictUserId,
          appType: APP_TYPE_PRACTICE_MANAGEMENT,
          deviceId: DEVICE_ID_WEB,
        });

        if (ctx.retryCredentials) {
          // Login path: re-issue /connect/token + full applyTokens chain.
          // Mirrors Flutter `:179-182` re-firing DoLoginEvent after
          // LogoutState.
          const fresh = await apiLogin(
            ctx.retryCredentials.username,
            ctx.retryCredentials.password,
          );
          pendingConflict = null;
          await applyTokens(fresh, ctx.retryCredentials);
          ctx.resolve();
        } else {
          // Refresh / boot path: retry the profile fetch on the existing
          // access token. The User_Tokens row is clear now so 200 should
          // follow.
          const apiUser = await getUserData(ctx.accessToken);
          const current = get().user ?? ({} as UserData);
          const merged = mergePreservingNonNull(current, apiUser);
          authStorage.setUser(merged);
          set({ user: merged, status: 'authenticated' });
          pendingConflict = null;
          ctx.resolve();
        }
      } catch (err) {
        // Takeover failed mid-retry. Surface to the awaiting promise +
        // bounce to login so the user can try again from scratch.
        pendingConflict = null;
        const fail = err instanceof Error ? err : new Error(String(err));
        ctx.reject(fail);
        await get().logout();
      }
    },

    confirmTimezoneRelogin: async () => {
      const ctx = pendingTzMismatch;
      if (!ctx) return;
      // Clear the parked context (and the credentials it holds) UP FRONT,
      // before any await, so a double-tap on OK can't capture the same ctx
      // twice and fire two re-logins (PR #473 review). `ctx` is the only
      // handle from here. Also clear the popup so the dialog dismisses.
      pendingTzMismatch = null;
      set({ timezoneMismatch: null });
      try {
        // Flutter `clearLocalDataWithoutChangingScreen()` — drop the local
        // session before re-login.
        authStorage.clear();
        // Ensure the server-side tz update has persisted before re-login, so
        // the fresh JWT's zoneinfo matches the device and we don't re-trigger
        // the mismatch. Best-effort; `skipTzPopup` below is the hard guard
        // against a popup loop.
        try {
          await updateUserTimeZone(ctx.accessToken, {
            userId: ctx.userId,
            timeZone: getLocalTimezone(),
            deviceToken: null,
            deviceType: DEVICE_TYPE_WEB,
            deviceId: DEVICE_ID_WEB,
          });
        } catch {
          // Non-fatal — re-login still proceeds; skipTzPopup prevents a loop.
        }
        // Re-issue /connect/token + full applyTokens chain, skipping the tz
        // popup so a still-stale zoneinfo can't loop (it would just complete
        // with the sentinel, i.e. the pre-PROD-1026 behavior). Mirrors Flutter
        // `login_screen.dart:134-135` re-firing DoLoginEvent.
        const fresh = await apiLogin(
          ctx.retryCredentials.username,
          ctx.retryCredentials.password,
        );
        await applyTokens(fresh, ctx.retryCredentials, { skipTzPopup: true });
        ctx.resolve();
      } catch (err) {
        // Re-login / clear failed mid-flow. Surface to the awaiting login()
        // promise + logout(), which resets `timezoneMismatch` to null → the
        // (non-dismissable) dialog closes and the user is returned to the
        // login form rather than being stuck (PR #473 review).
        const fail = err instanceof Error ? err : new Error(String(err));
        ctx.reject(fail);
        await get().logout();
      }
    },

    cancelTakeover: async () => {
      const ctx = pendingConflict;
      pendingConflict = null;
      if (ctx) {
        ctx.reject(
          new Error('Active session takeover cancelled by user'),
        );
      }
      // Cancel = full logout so the user goes back to /login. Match the
      // Flutter `afterNo` callback semantics (`login_screen.dart:148-150`
      // sets `isActiveSessionPopUp = false` and the user remains on the
      // login screen).
      await get().logout();
    },

    getValidAccessToken: async () => {
      const { accessToken } = get();
      // PROD-926: DEV_CONFIG bootstrap removed. Unauthenticated callers
      // throw — the App.tsx redirect guard then bounces them to /login.
      // No silent re-auth path remains, so logout works in every env +
      // the landing page is /login when there's no session.
      if (!accessToken) throw new Error('Not authenticated');
      // Idle-timeout gate BEFORE the proactive refresh (Flutter parity):
      // an idle session past the cap expires instead of silently rotating
      // its token. `noteActivity` also resets the clock on in-window calls.
      if (noteActivity() === 'idle-expired') throw new SessionIdleExpiredError();
      if (!isExpiringSoon(accessToken)) return accessToken;
      return doRefresh();
    },

    forceRefresh: () => doRefresh(),

    markOnboardingComplete: () => {
      const current = get().user;
      if (!current) {
        // Defensive bail — the wizard mount gate in App.tsx requires
        // `user?.role === '1'`, so reaching this branch means storage
        // was wiped mid-wizard (logout race, manual localStorage clear,
        // ...). Log loudly because losing the write would strand the
        // user in the wizard forever on next refresh.
        // eslint-disable-next-line no-console
        console.error(
          '[auth] markOnboardingComplete called with null user — wizard write skipped',
        );
        return false;
      }
      // `isOnboarded` is typed `boolean | undefined` on UserData. The
      // wizard gate in App.tsx checks `user?.isOnboarded !== true`, so
      // writing a boolean literal (NOT the string "true") is what flips
      // it. Past mobile-side bugs with string-serialised booleans —
      // keep this explicit.
      const next: UserData = { ...current, isOnboarded: true };
      authStorage.setUser(next);
      set({ user: next });
      return true;
    },

    setActivePractice: ({ practiceSetupId, practiceName }) => {
      const current = get().user;
      // Defensive bail — the switcher only renders inside the authenticated
      // app shell, so a null user here means storage was wiped mid-session.
      if (!current || !practiceSetupId) return false;
      // No-op if the user picked the practice that's already active — avoids
      // a needless storage write + query-cache churn.
      if (current.practiceId === practiceSetupId) return false;
      const next: UserData = {
        ...current,
        practiceId: practiceSetupId,
        practiceName: practiceName ?? undefined,
      };
      // Persistence note (PR review): `authStorage.setUser` serialises the
      // whole UserData blob to localStorage — the established session posture
      // (see storage.ts: XSS-readable, mirrors Flutter Web's sessionStorage
      // fallback). `practiceName` is a CLINIC/org name, not patient PHI, and
      // it's no more sensitive than the firstName/lastName/email/role already
      // persisted at login. We keep it (not just the id) so the toolbar chip
      // renders the correct label on reload before the practices list
      // resolves. PHI is never written here.
      authStorage.setUser(next);
      set({ user: next });
      return true;
    },
  };
});

/**
 * Map an `ApiAuthError` thrown by `/connect/token` to the Flutter-verbatim
 * user-facing error string from `login_repository_impl.dart:58-101`.
 *
 * On status 400, reactively calls `checkLockOutTime(email)` to disambiguate
 * "wrong password" vs "account locked" — matches Flutter's
 * `loginDataSource!.checkLocked(email: email)` call at `:70`.
 *
 * Returns an `Error` whose message is the verbatim Flutter string; caller
 * re-throws so the global toast pipeline picks it up.
 */
async function mapLoginError(err: ApiAuthError, email: string): Promise<Error> {
  if (err.status === 401) {
    return new Error(
      'Your account is archived or inactive, please contact Scrips.',
    );
  }
  if (err.status === 400) {
    const lock = await checkLockOutTime(email);
    if (lock.minute != null) {
      return new Error(
        `Your Account is locked, please try after ${Math.floor(lock.minute)} min.`,
      );
    }
    return new Error('Invalid Email Address or Incorrect Password');
  }
  if (err.status === 422) {
    return new Error(
      'Device limit reached, you can not login with this device',
    );
  }
  if (err.status === 404) {
    return new Error('Failed to authorize from server.');
  }
  // 409 multi-session conflict and other unmapped statuses fall through
  // to a generic message. Phase 1+ may surface the 409 ActiveSession popup
  // (`login_screen.dart:144-152`); Phase 1 falls through.
  return new Error('Login failed. Please try again.');
}

// One-time profile refresh on app boot. The cached `scrips.user` blob in
// localStorage may pre-date fields we now rely on (e.g. organizationLocations
// for the location-filter buttons), so re-fetch the rich profile from the API
// whenever we boot with a token. Failures are silent — JWT-only user is the
// floor and the app keeps working.
//
// Hits `CheckUserDataToken` (not `GetUserDataToken`) — mirrors Flutter
// `MenuContainerScreen.initState → GetUserDataByTokenEvent → CheckUserDataToken`
// at `menu_container_screen.dart:104` + `login_data_source_impl.dart:391-440`.
// Same response shape; different endpoint for the cold-start path.
//
// Uses the same `mergePreservingNonNull` strategy as doRefresh (PR #320) so
// a partial response doesn't overwrite valid cached fields with nulls.
// Boot-time 401/403 stay swallowed: if the cached JWT is already invalid,
// the api-client will hit 401 on the next real call and forceRefresh will
// fire — at which point the strict 401/403 → logout path in doRefresh
// takes over.
//
// 409 (active-session conflict) — park `pendingConflict` and surface the
// dialog. No awaiting promise here (boot is fire-and-forget); the user's
// confirm/cancel drives the resolution. Mirrors Flutter
// `login_repository_impl.dart:188-198` for the `CheckUserDataToken` path.
//
// EXPORTED + called once from `main.tsx` — NOT a module-init side effect.
// Making it explicit (rather than a bare IIFE at import time) stops an async
// `CheckUserDataToken` fetch from firing on EVERY test that imports this
// module; those stray boot fetches raced across the shared `fetch` mock and
// made the whole auth suite intermittently flaky. Production behaviour is
// unchanged — `main.tsx` calls this once at startup; boot-path tests call it
// explicitly.
export async function resyncUserOnBoot(): Promise<void> {
  if (typeof window === 'undefined') return;
  const { accessToken, refreshToken, user } = useAuthStore.getState();
  if (!accessToken) return;
  try {
    const apiUser = await checkUserData(accessToken);
    const merged = mergePreservingNonNull((user ?? {}) as UserData, apiUser);
    authStorage.setUser(merged);
    useAuthStore.setState({ user: merged });
  } catch (err) {
    if (err instanceof ActiveSessionConflictError) {
      // Park the conflict — dialog opens; user confirms or cancels. No promise
      // to resolve (boot has no caller waiting), so use no-op resolvers and
      // let the takeover actions handle the rest.
      pendingConflict = {
        conflictUserId: err.conflictUserId,
        accessToken,
        refreshToken,
        retryCredentials: null,
        resolve: () => {},
        reject: () => {},
      };
      useAuthStore.setState({
        activeSessionConflict: { conflictUserId: err.conflictUserId },
      });
      return;
    }
    // Other network/profile errors don't block app boot.
  }
}
